import { ApiUrl } from 'src/constants';
import { toQueryString } from 'src/helpers/buildQueryParamsString';
import { httpService } from 'src/services';
import { HydraResponse } from 'src/api/hydraResponse';
import { IComplex } from 'src/typings/complexes';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const normalizeTimeZone = (timeZone: string): string => (
  timeZone === 'Europe/Kiev' ? 'Europe/Kyiv' : timeZone
);

export interface AvailabilityExportOptions {
  language?: string;
  timeZone?: string;
  signal?: AbortSignal;
}

interface AvailabilityReportResponse {
  blob: Blob;
  filename: string;
}

export class AvailabilityExportError extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = 'AvailabilityExportError';
  }
}

const getFallbackFilename = (date: Date, timeZone: string): string => {
  const parts = new Intl.DateTimeFormat('en-US-u-ca-gregory-nu-latn', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const getPart = (name: string) => parts.find((part) => part.type === name)?.value || '';

  return `Availability report ${getPart('month')}.${getPart('day')}.${getPart('year')}.xlsx`;
};

const getResponseError = async (response: Response): Promise<AvailabilityExportError> => {
  const error = await response.json().catch(() => null);
  const message = typeof error?.message === 'string'
    ? error.message
    : `Availability export failed (${response.status}).`;

  return new AvailabilityExportError(response.status, message);
};

export const getAvailabilityComplexes = (
  token: string,
  userId: string,
): Promise<HydraResponse<IComplex[]>> => {
  const params = toQueryString({
    users: userId,
    properties: {
      0: 'id',
      1: 'title',
      2: 'language',
      developers: ['id', 'name', 'language'],
    },
  });

  return httpService.get(
    `${ApiUrl.getApiUrl().getComplexes}?${params}&pagination=false&status=ACTIVE&hasAvailablePlacements=true`,
    {},
    { Authorization: `Bearer ${token}` },
  ).then((response: HydraResponse<IComplex[]>) => {
    if (!response.ok || !response.data?.['hydra:member']) {
      const responseData = response.data as unknown as {'hydra:description'?: string, message?: string};
      const message = responseData?.['hydra:description']
        || responseData?.message
        || `Failed to load projects (${response.status || 'unknown'}).`;
      throw new Error(message);
    }

    return response;
  });
};

export const getAvailabilityReport = async (
  token: string,
  selectedIds: string[],
  options: AvailabilityExportOptions = {},
): Promise<AvailabilityReportResponse> => {
  if (!selectedIds.length || selectedIds.length > 250) {
    throw new Error('Select 1 to 250 projects.');
  }

  const complexIds = [...new Set(selectedIds)];
  const timeZone = normalizeTimeZone(
    options.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const filenameFallback = getFallbackFilename(new Date(), timeZone);
  const response = await fetch(ApiUrl.getApiUrl().exportAvailability, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: XLSX_MIME,
      'Content-Type': 'application/json',
    },
    signal: options.signal,
    body: JSON.stringify({
      complexIds,
      timeZone,
      ...(options.language !== undefined ? { language: options.language } : {}),
    }),
  });

  if (!response.ok) {
    throw await getResponseError(response);
  }

  const contentType = response.headers.get('Content-Type')?.split(';')[0].trim();
  if (response.status !== 200 || contentType !== XLSX_MIME) {
    throw new Error('The server returned an unexpected export response.');
  }

  const blob = await response.blob();
  if (options.signal?.aborted) {
    throw new DOMException('Export cancelled.', 'AbortError');
  }
  if (!blob.size) {
    throw new Error('The server returned an empty file.');
  }

  const contentDisposition = response.headers.get('Content-Disposition') || '';
  const suppliedFilename = /filename="([^"]+)"/i.exec(contentDisposition)?.[1];
  const filename = suppliedFilename
    && /^Availability report \d{2}\.\d{2}\.\d{4}\.xlsx$/.test(suppliedFilename)
    ? suppliedFilename
    : filenameFallback;

  return { blob, filename };
};
